import React, { useCallback, useState, useEffect } from "react"
import { Container, Row, Label, FormFeedback, Col, Card, CardBody, Input, Form } from "reactstrap"
import { useDropzone } from "react-dropzone"
import { useFormik } from "formik"
import * as Yup from "yup"
import { isPermission, uId } from "common/utility"
import * as XLSX from "xlsx";
import Bulk_upload_order from '../Orders/Bulk_upload_order.xlsx';
import ToastDialog from "components/Common/ToastDialog";
import { callUplodBulkOrderEmployee } from "../../http/auth/signup"
import { useNavigate } from "react-router-dom"
import { uploadInquiryFile } from "helpers/UAM/inquiry.helper";
import Select from "react-select";
import { getDealer, getShipToPartyList } from "helpers/UAM/dealer.helper";
import {
    getDealerUserList,
} from "helpers/UAM/user.helper"
import { getAddress } from "http/address/address"
import BulkEmpOrder from "./empBulkTable"
import { getSystemConfigurationByKey } from "helpers/UAM/systemConfig.helper";

const EmployeeUploadBulkOrder = () => {
    document.title = "Upload Orders Request | DMS"
    const [uploadProgress, setUploadProgress] = useState(0)
    const [showTable, setShowTable] = useState(true)
    const errorMessage = null
    const [toastInfo, setToastInfo] = useState({ showToast: false })
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem("user"));
    const [dealers, setDealers] = useState([]);
    const [dealerUsers, setDealerUsers] = useState([]);
    const [dealerId, setDealerId] = useState(null);
    const [addressList, setAddressList] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [maxLimit, setMaxLimit] = useState(50)
    const [uploadPermission, setUploadPermission] = useState(false);
    const [shipToPartyList, setShipToPartyList] = useState([]);


    const onDrop = useCallback(acceptedFiles => {
        const selectedFile = acceptedFiles[0]
        if (selectedFile) {
            validation.setFieldValue("fileUpload", selectedFile)
            setUploadProgress(100)
        } else {
            validation.setFieldValue("fileUpload", [])
        }
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
    })

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            dealer: "",
            dealerUser: "",
            address: "",
            poDate: "",
            poNumber: "",
            fileUpload: "",
            shipToParty: "",
        },
        validationSchema: Yup.object({
            dealer: Yup.string().required("Please Select Dealer"),
            dealerUser: Yup.string().required("Please Select Dealer User"),
            address: Yup.string(),
            shipToParty: Yup.string(),
            poDate: Yup.date(),
            poNumber: Yup.string(),
            fileUpload: Yup.mixed()
                .required("Please Uplaod File")
                .test(
                    "fileType",
                    "Invalid file type",
                    value => {
                        if (value) {
                            const allowedExtensions = [".xls", ".xlsx"];
                            const fileExtension = value.name
                                .split(".")
                                .pop()
                                .toLowerCase();
                            return allowedExtensions.includes(`.${fileExtension}`);
                        }
                        return true;
                    }

                ),
        }),
        onSubmit: values => {
            handleSheetData(values.fileUpload, async (json) => {
                let jsonData = json.json;
                let numColumns = json.numColumns;

                try {
                    if (!jsonData || jsonData.length === 0) {
                        showToast('Add Atleast One Order');
                    } else if (jsonData.length > maxLimit) {
                        showToast(`Maximum limit of ${maxLimit} items exceeded. Please reduce the number of items.`);
                    } else if (numColumns !== 2) {
                        showToast('Invalid Form Data');
                    } else {
                        await handleFileUploadAndCall(values, jsonData);
                    }
                } catch (err) {
                    console.log(err);
                }
            });
        },
    })

    const showToast = (message, role = "error") => {
        setToastInfo({
            message,
            showToast: true,
            role,
        });
    };

    const handleSuccess = (message, batchId) => {
        setToastInfo({
            message,
            showToast: true,
            role: "success",
        });

        const encryptedId = btoa(batchId.toString());
        setTimeout(() => {
            navigate(`/${user.roleName}/uploadbulkorderstatus?id=${encodeURIComponent(encryptedId)}`);
        }, 1000);
        validationFileUpload.resetForm();
    };

    const handleError = (err) => {
        console.log(err);
    };

    const handleCancel = () => {
        setShowTable(!showTable)
    }

    const handleFileUploadAndCall = async (values, jsonData) => {
        try {
            const res = await handleFileUpload(values.fileUpload);

            if (res.status) {
                const obj = {
                    address: values.address,
                    dealerId: values.dealer,
                    dealerUserId: values.dealerUser,
                    poDate: values.poDate,
                    poNumber: values.poNumber,
                    shipToParty: values.shipToParty,
                    products: jsonData,
                };

                const uploadRes = await callUplodBulkOrderEmployee({
                    filePath: res.path,
                    noOfRecords: jsonData.length,
                    fileData: obj,
                });

                if (uploadRes.status) {
                    handleSuccess(uploadRes.message.message, uploadRes.message.batchId);
                } else {
                    showToast(uploadRes.message, "error");
                }
            } else {
                showToast(res.path);
            }
        } catch (err) {
            handleError(err);
        }
    };


    useEffect(() => {
        checkPermissions()
        getDealerList({ searchKey: '' })
        getDataLimitFunc()
    }, []);

    useEffect(() => {
        getDealerUsersList({ entityId: dealerId, searchKey: searchValue })
        getAddressList({ entityId: dealerId })
        fetchShipToPartyList(dealerId)
    }, [dealerId, searchValue]);

    const getDataLimitFunc = async () => {
        let limit = await getSystemConfigurationByKey({ key: 'Data_limit' })
        if (limit.status) {
            let max = JSON.parse(limit.data[0].configValue)
            max = max.bulk_order ? Number(max.bulk_order) : 50
            setMaxLimit(max)
        }
    }

    const getDealerList = async (bodyData) => {
        const response = await getDealer(bodyData);
        if (response?.status) {
            setDealers(response.data.data.map(item => {
                return { label: item['dealerName'] + ' ' + `(${item['dealerCode']})`, value: item['id'] }
            }));
        } else {
            setDealers([])
        }
    }

    const getDealerUsersList = async (bodyData) => {
        const response = await getDealerUserList(bodyData);
        if (response.status) {
            setDealerUsers(response.data.data.map(item => {
                let fullName = item['firstName'] + ' '
                if (item['lastName']) {
                    fullName += item['lastName']
                }
                return { label: fullName, value: item['id'] }
            }));
        } else {
            setDealerUsers([])
        }
    }

    const getAddressList = async (bodyData) => {
        const response = await getAddress(bodyData);
        if (response.status) {
            setAddressList(response.data.data.map(item => {
                let formattedAddress = formatAddress(item)
                return { label: formattedAddress, value: item['id'] }
            }));
        } else {
            setAddressList([])
        }
    }

    const handleInputChange = (value) => {
        getDealerList({ searchKey: value })
    };

    const handleInputChangeUser = (value) => {
        setSearchValue(value)
    };

    const checkPermissions = async () => {
        const isUploadPermission = await isPermission("bulk_upload_orders", "upload_permission")
        setUploadPermission(isUploadPermission)
    }

    const renderHeaderText = () => {
        if (showTable) {
            return (
                <>
                    <i className="bx bx-folder me-2"></i>
                    Upload Orders
                </>
            );
        } else {
            return <>Show File Status</>;
        }
    };

    const getSelectClassName = (value) => {
        return `select-dropdown ${validation.touched[value] && validation.errors[value] ? "is-invalid" : ""}`;
    };

    const fetchShipToPartyList = async (id) => {
        try {
            let res = await getShipToPartyList({ dealerId: id })
            if (!res.status) {
                setShipToPartyList([])
            }
            setShipToPartyList(res?.data || [])
        } catch (e) {
            setShipToPartyList([])
        }
    }

    return (
        <div className="page-content">
            <Container fluid>
                <div className='page-title-box d-flex gap-3 justify-content-between align-items-center'>
                    <h4 className="mb-sm-0 font-size-18">UPLOAD ORDERS REQUEST</h4>
                    <div>
                        {uploadPermission && (<button
                            className="btn btn-outline-success"
                            type="button"
                            onClick={() => {
                                setShowTable(!showTable)
                            }}
                        >
                            {renderHeaderText()}
                        </button>)}
                    </div>
                </div>
                <Card>
                    <CardBody>
                        <Row className="vertical-scroll">
                            {!showTable && (<div>
                                <div className="mt-4">
                                    <div
                                        style={{ display: "flex", justifyContent: "flex-end", width: "100%" }}
                                    >
                                        <div className="d-flex">
                                            <div>
                                                <h6>Download Order Sample File:</h6>
                                            </div>
                                            <div
                                                style={{
                                                    backgroundColor: "#344fa0",
                                                    color: "white",
                                                    padding: "8px 10px 5px 10px",
                                                    borderRadius: "100%",
                                                    fontSize: "20px",
                                                    marginTop: "-12px",
                                                    cursor: "pointer",
                                                }}
                                                className="ms-2"
                                                onClick={() => {
                                                    downloadExcelFile()
                                                    setToastInfo({
                                                        message: 'File Downlaoded Successfully',
                                                        showToast: true,
                                                        role: "success",
                                                    })
                                                }}
                                            >
                                                <i className="bx bx-archive-in" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <Row >
                                    <div className="bulk">
                                        <Form
                                            onSubmit={e => {
                                                e.preventDefault()
                                                validation.handleSubmit()
                                                // validationFileUpload.handleSubmit()
                                                return false
                                            }}
                                        >
                                            <Row className="mb-4">

                                                <Col sm="12" md="6">
                                                    <Label className="form-label  required">Select Dealer
                                                    </Label>
                                                    <Select
                                                        options={dealers}
                                                        className={getSelectClassName("dealer")}
                                                        name="dealer"
                                                        onInputChange={handleInputChange}
                                                        id="dealer"
                                                        onChange={(selectedOption) => {
                                                            validation.setFieldValue("dealer", selectedOption.value);
                                                            setDealerId(selectedOption.value)
                                                        }}
                                                        value={dealers.find((option) => option.value == validation.values.dealer)}

                                                    />
                                                    {validation.touched.dealer && validation.errors.dealer ? (
                                                        <FormFeedback type="invalid">{validation.errors.dealer}</FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-3 mt-lg-0 required">Select Dealer User
                                                    </Label>
                                                    <Select
                                                        options={dealerUsers}
                                                        className={getSelectClassName("dealerUser")}
                                                        name="dealerUser"
                                                        onInputChange={handleInputChangeUser}
                                                        id="dealerUser"
                                                        onChange={(selectedOption) => {
                                                            validation.setFieldValue("dealerUser", selectedOption.value);
                                                        }}
                                                        value={dealers.find((option) => option.value == validation.values.dealerUser)}

                                                    />
                                                    {validation.touched.dealerUser && validation.errors.dealerUser ? (
                                                        <FormFeedback type="invalid">{validation.errors.dealerUser}</FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-3">Select Address
                                                    </Label>
                                                    <Select
                                                        options={addressList}
                                                        className={getSelectClassName("address")}
                                                        name="address"
                                                        onInputChange={handleInputChangeUser}
                                                        id="address"
                                                        onChange={(selectedOption) => {
                                                            validation.setFieldValue("address", selectedOption.value);
                                                        }}
                                                        value={dealers.find((option) => option.value == validation.values.address)}

                                                    />
                                                    {validation.touched.address && validation.errors.address ? (
                                                        <FormFeedback type="invalid">{validation.errors.address}</FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-3">PO Number</Label>
                                                    <Input
                                                        name="poNumber"
                                                        type="text"
                                                        placeholder="Enter PO Number"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.poNumber || ""}
                                                        invalid={
                                                            validation.touched.poNumber &&
                                                            validation.errors.poNumber
                                                        }
                                                    />
                                                    {validation.touched.poNumber &&
                                                        validation.errors.poNumber ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.poNumber}
                                                        </FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-3">PO Date</Label>
                                                    <Input
                                                        type="date"
                                                        id="poDate"
                                                        max={new Date(Date.now()).toISOString().split('T')[0]}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.poDate || ""}
                                                        invalid={
                                                            validation.touched.poDate &&
                                                            validation.errors.poDate
                                                        }
                                                    />
                                                    {validation.touched.poDate && validation.errors.poDate ? (
                                                        <FormFeedback type="invalid">{validation.errors.poDate}</FormFeedback>
                                                    ) : null}
                                                </Col>

                                                <Col sm="12" md="6">
                                                    <Label className="form-label mt-3">Select Ship to Party
                                                    </Label>
                                                    <Select
                                                        options={shipToPartyList.map((option) => ({
                                                            label: option.partnerCode + ' ' + `(${option.address})`,
                                                            value: option.partnerCode,
                                                        }))}
                                                        className={getSelectClassName("shipToParty")}
                                                        name="shipToParty"
                                                        id="shipToParty"
                                                        onChange={(selectedOption) => {
                                                            validation.setFieldValue("shipToParty", selectedOption.value);
                                                        }}
                                                        value={shipToPartyList.find((option) => option.value == validation.values.shipToParty)}

                                                    />
                                                    {validation.touched.shipToParty && validation.errors.shipToParty ? (
                                                        <FormFeedback type="invalid">{validation.errors.shipToParty}</FormFeedback>
                                                    ) : null}
                                                </Col>
                                            </Row>
                                            <div className="bulkOrderUpload" style={{ gap: "1rem", padding: '2rem 0rem' }}>
                                                <div className="bulkOrderUploadImgdiv">
                                                    <img src="/bulkOrderUpload.svg" alt="file image " />
                                                </div>
                                                <div>
                                                    <div className="heading"> Upload Orders </div>
                                                    <div>
                                                        <p>Upload your bulk Orders.</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="d-flex flex-column">
                                                        <Label
                                                            htmlFor="horizontal-email-Input"
                                                            className="required-sign"
                                                        >
                                                            {"Upload File ( .xlsx, xls)"}
                                                            <i className="mdi mdi-asterisk"></i>
                                                        </Label>
                                                        <div
                                                            className="drop"
                                                            style={{ padding: "1rem" }}
                                                            onKeyUp={() => { }}
                                                            onClick={() => {
                                                                validation.touched.fileUpload = true
                                                            }}
                                                        >
                                                            <div {...getRootProps()} className="text-wrapper">
                                                                <div className="centered">
                                                                    <input
                                                                        type="file"
                                                                        name="formatDocument"
                                                                        id="itrFileExcel"
                                                                        accept=".jpg, .png, .jpeg, .pdf"
                                                                        {...getInputProps()}
                                                                    />
                                                                    <label
                                                                        style={{
                                                                            marginBottom: 0,
                                                                            fontSize: "14px",
                                                                        }}
                                                                    >
                                                                        {isDragActive && (
                                                                            <p className="textLink">Drop here</p>
                                                                        )}
                                                                        <span className="textLink">
                                                                            Browse files to attach
                                                                        </span>
                                                                        or Drop
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <label style={{ color: '#344fa0' }}>
                                                            {`Ensure Excel file data does not exceed ${maxLimit} records`}
                                                        </label>
                                                        {validation.values.fileUpload && (
                                                            <>
                                                                <span className="mt-2" key={`file-${uId()}`}>
                                                                    {validation.values.fileUpload.name}{" "}
                                                                </span>
                                                                {uploadProgress > 0 && !errorMessage && (
                                                                    <div className="progress mt-2">
                                                                        <div
                                                                            className="progress-bar bg-success"
                                                                            style={{ width: `${uploadProgress}%` }}
                                                                            aria-valuenow="0"
                                                                            aria-valuemin="0"
                                                                            aria-valuemax="100"
                                                                        >
                                                                            {`${uploadProgress}%`}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {errorMessage && (
                                                                    <div className="alert alert-danger mt-2">
                                                                        {errorMessage}
                                                                    </div>
                                                                )}{" "}
                                                            </>
                                                        )}

                                                        {validation.submitCount > 0 &&
                                                            validation.errors.fileUpload ? (
                                                            <FormFeedback type="invalid" style={{ display: "block" }}>
                                                                {validation.errors.fileUpload}
                                                            </FormFeedback>
                                                        ) : null}

                                                    </div>
                                                    <hr />
                                                </div>
                                            </div>
                                            <Row className="mt-2">
                                                <Col>
                                                    <div className="mt-2 d-flex gap-3 justify-content-end">
                                                        <button
                                                            className="btn btn-outline-secondary"
                                                            type="button"
                                                            onClick= {handleCancel}
                                                        >
                                                        Cancel
                                                        </button>

                                                        <button
                                                            type="submit"
                                                            className="btn btn-success save-customer"
                                                        >
                                                            Upload
                                                        </button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </div>
                                </Row>
                            </div>)}
                            {showTable && (
                                <BulkEmpOrder />
                            )}
                        </Row>
                    </CardBody>
                </Card>
            </Container>
            <ToastDialog
                message={toastInfo.message}
                showToast={toastInfo.showToast}
                role={toastInfo.role}
                onCloseFn={() => setToastInfo({ showToast: false })}
            />
        </div>
    )
}

export default EmployeeUploadBulkOrder

const handleSheetData = (file, callback) => {
    if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = e.target.result;
            const workbook = XLSX.read(data, { type: "binary" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const range = XLSX.utils.decode_range(worksheet['!ref']);
            const numColumns = range.e.c - range.s.c + 1;
            const json = XLSX.utils.sheet_to_json(worksheet, {
                defval: null,
                raw: false,
            });
            callback({ json, numColumns });
        };

        reader.readAsBinaryString(file);
    } else {
        callback(null);
    }
};

function downloadExcelFile() {
    const link = document.createElement("a");
    link.href = Bulk_upload_order;
    link.download = "Bulk_upload_order.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

const handleFileUpload = async (uploadedFile) => {
    if (!uploadedFile) return;
    const fileUploadApiRes = await uploadInquiryFile(uploadedFile);
    if (fileUploadApiRes.status) {
        return { path: fileUploadApiRes.data[0], status: true }
    }
    return { path: fileUploadApiRes.message || 'Something Went Wrong', status: false }
}

function formatAddress(address) {
    const { address1, address2, address3, city, country, pincode, state } = address;
    const addressArray = [address1, address2, address3, city, country, pincode, state].filter(value => value !== null && value !== undefined);
    const formattedAddress = addressArray.join(', ');
    return formattedAddress;
}